import { useMeasurementPresentation } from '../../Measurement'
import { optionalVehicleNumber } from '../../../lib/vehicleFormContract'
import React, { useCallback, useMemo, useState, useRef } from 'react'
import { Vehicle, AxleConfigEntry } from '../../../types/optimization'
import type { VehicleAxleConfiguration } from '../../../types/vehiclePhysics'
import { Disc3, Trash2 } from 'lucide-react'
import { AxleDiagram } from './AxleDiagram'
import { axleMembersLabel, numberedAxles, updateVehicleCoupling } from '../../../lib/vehicleAxleDiagram'

interface AxleConfigurationProps {
  vehicle: Vehicle | null | undefined
  onChange: (updatedVehicle: Vehicle) => void
  mode?: 'edit' | 'view'
  /** Fallback fields while the dimensions or axle profile cannot be drawn. */
  couplingControls?: React.ReactNode
}

interface AxleGroup {
  type: 'tandem' | 'tridem'
  axles: number[]
  startIndex: number
}

// Couleurs spécifiques par type d'essieu
export function getAxleTypeColor(type?: string): string {
  switch (type) {
    case 'steering':
      return '#3b82f6' // Bleu (Directeur)
    case 'drive':
      return '#10b981' // Vert Émeraude (Moteur)
    case 'trailer':
      return '#a855f7' // Violet (Remorque)
    case 'tag':
      return '#6366f1' // Indigo (Tag)
    default:
      return '#f59e0b' // Ambre (Autre)
  }
}

export function getAxleTypeBgClass(type?: string): string {
  switch (type) {
    case 'steering':
      return 'bg-blue-500'
    case 'drive':
      return 'bg-emerald-500'
    case 'trailer':
      return 'bg-purple-500'
    case 'tag':
      return 'bg-indigo-500'
    default:
      return 'bg-amber-500'
  }
}

const inputCls = "w-full px-2.5 py-1.5 text-xs rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
const selectCls = "w-full px-2.5 py-1.5 text-xs rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"

export const AxleConfiguration: React.FC<AxleConfigurationProps> = ({
  vehicle,
  onChange,
  mode = 'edit',
  couplingControls
}) => {
  const currentConfig = typeof vehicle?.axle_configuration === 'object'
    ? (vehicle?.axle_configuration as VehicleAxleConfiguration)
    : null
  const currentPhysicsType = currentConfig?.physicsType || 'custom'
  const isContainer = vehicle?.type === 'CONTAINER' || currentPhysicsType === 'container'

  if (isContainer) {
    return (
      <div className="rounded-xl p-4 space-y-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Disc3 className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">Configuration des Essieux</h3>
        </div>
        <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-medium text-slate-800 dark:text-slate-200">
            Ce type de véhicule (conteneur) n'admet pas d'essieux.
          </p>
          <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
            Les conteneurs sont transportés par des camions ou tracteurs et n'ont pas leurs propres essieux.
          </p>
        </div>
      </div>
    )
  }

  if (!currentConfig) {
    return (
      <>
      <div className="rounded-xl p-4 space-y-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Disc3 className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">Configuration des Essieux</h3>
        </div>
        <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-medium text-slate-800 dark:text-slate-200">
            La configuration des essieux n'est pas définie pour ce véhicule.
          </p>
          <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
            Sélectionnez un profil véhicule pour configurer automatiquement les essieux.
          </p>
        </div>
      </div>
      {couplingControls}
      </>
    )
  }

  return <AxleConfigurationEditor vehicle={vehicle} onChange={onChange} mode={mode} couplingControls={couplingControls} />
}

// Applicability changes mount/unmount the editor instead of changing hook order.
function AxleConfigurationEditor({ vehicle, onChange, mode = 'edit', couplingControls }: AxleConfigurationProps) {
  const { format: formatMeasure } = useMeasurementPresentation()
  const groupHelpId = React.useId()
  const currentConfig = vehicle!.axle_configuration as VehicleAxleConfiguration
  const axles = currentConfig.axles || []

  const [activeAxle, setActiveAxle] = useState(0)
  const [groupsOpen, setGroupsOpen] = useState(false)
  const axleNameRefs = useRef<Array<HTMLInputElement | null>>([])
  const groupNameRefs = useRef<Array<HTMLInputElement | null>>([])
  const addGroupRef = useRef<HTMLButtonElement>(null)

  const knownLength = optionalVehicleNumber(vehicle?.dimensions?.length ?? vehicle?.length)
  const knownWidth = optionalVehicleNumber(vehicle?.dimensions?.width ?? vehicle?.width)
  const vehicleLength = knownLength ?? 0
  const vehicleWidth = knownWidth ?? 0

  // Auto-détection des groupes tandem / tridem
  const axleGroups = useMemo(() => {
    if (!currentConfig?.axles || currentConfig.axles.length < 2) return []

    const groups: AxleGroup[] = []
    const axlesSorted = [...currentConfig.axles].sort((a, b) => a.position - b.position)
    const used = new Set<number>()

    for (let i = 0; i < axlesSorted.length - 2; i++) {
      if (used.has(i)) continue
      const d1 = axlesSorted[i + 1].position - axlesSorted[i].position
      const d2 = axlesSorted[i + 2].position - axlesSorted[i + 1].position
      if (d1 <= 1400 && d2 <= 1400) {
        groups.push({ type: 'tridem', axles: [i, i + 1, i + 2], startIndex: i })
        used.add(i); used.add(i + 1); used.add(i + 2)
        i += 2
      }
    }

    for (let i = 0; i < axlesSorted.length - 1; i++) {
      if (used.has(i)) continue
      const d = axlesSorted[i + 1].position - axlesSorted[i].position
      if (d <= 1300) {
        groups.push({ type: 'tandem', axles: [i, i + 1], startIndex: i })
        used.add(i); used.add(i + 1)
        i++
      }
    }

    return groups
  }, [currentConfig?.axles])
  const configuredGroups = (currentConfig.axleGroups || []).map(group => ({
    ...group,
    axleIds: group.axleIds || (group as any).axles || []
  }))

  const handleUpdateAxle = useCallback((index: number, field: keyof AxleConfigEntry, value: any) => {
    if (!currentConfig) return
    const updatedAxles = [...axles]
    const previousId = updatedAxles[index].id
    updatedAxles[index] = { ...updatedAxles[index], [field]: value }
    const axleGroups = field === 'id' && previousId !== value
      ? (currentConfig.axleGroups || []).map(group => ({
        ...group,
        axleIds: (group.axleIds || []).map(id => id === previousId ? value : id)
      }))
      : currentConfig.axleGroups
    onChange({
      ...vehicle,
      axle_configuration: {
        ...currentConfig,
        axles: updatedAxles,
        axleGroups
      } as any,
    })
  }, [vehicle, currentConfig, axles, onChange])

  const handleAddAxle = useCallback(() => {
    if (!currentConfig) return
    const updatedAxles = [...axles]
    let newIndex = updatedAxles.length + 1
    while (updatedAxles.some(axle => axle.id === `axle_${newIndex}`)) newIndex++
    const lastPos = updatedAxles.length > 0 ? updatedAxles[updatedAxles.length - 1].position + 1200 : 2000
    const newPos = Math.min(vehicleLength, lastPos)

    updatedAxles.push({
      id: `axle_${newIndex}`,
      position: newPos,
      maxWeight: 9000,
      type: 'trailer',
      emptyWeight: undefined
    })

    onChange({
      ...vehicle,
      axle_configuration: {
        ...currentConfig,
        axles: updatedAxles
      } as any,
    })
    setActiveAxle(updatedAxles.length - 1)
    requestAnimationFrame(() => axleNameRefs.current[updatedAxles.length - 1]?.focus())
  }, [vehicle, currentConfig, axles, vehicleLength, onChange])

  const handleRemoveAxle = useCallback((index: number) => {
    if (!currentConfig) return
    const updatedAxles = [...axles]
    const removedId = updatedAxles[index]?.id
    updatedAxles.splice(index, 1)
    setActiveAxle(Math.min(index, updatedAxles.length - 1))
    onChange({
      ...vehicle,
      axle_configuration: {
        ...currentConfig,
        axles: updatedAxles,
        axleGroups: (currentConfig.axleGroups || [])
          .map(group => ({ ...group, axleIds: (group.axleIds || []).filter(id => id !== removedId) }))
          .filter(group => group.axleIds.length >= 2)
      } as any,
    })
    requestAnimationFrame(() => axleNameRefs.current[Math.min(index, updatedAxles.length - 1)]?.focus())
  }, [vehicle, currentConfig, axles, onChange])

  const updateGroups = useCallback((groups: any[]) => {
    if (!currentConfig) return
    onChange({ ...vehicle, axle_configuration: { ...currentConfig, axleGroups: groups } as any })
  }, [vehicle, currentConfig, onChange])

  const addDetectedGroups = useCallback(() => {
    const groups = axleGroups.map((group, index) => ({
      id: `${group.type}_${index + 1}`,
      type: group.type,
      axleIds: group.axles.map(index => [...axles].sort((a, b) => a.position - b.position)[index].id),
      maxWeight: undefined,
      autoDetected: true,
      limitSource: ''
    }))
    updateGroups(groups)
  }, [axleGroups, axles, updateGroups])

  const addGroup = useCallback(() => {
    setGroupsOpen(true)
    const nextIndex = configuredGroups.length + 1
    updateGroups([...configuredGroups, {
      id: `groupe_${nextIndex}`,
      type: 'tandem',
      axleIds: [],
      maxWeight: undefined,
      autoDetected: false,
      limitSource: ''
    }])
    requestAnimationFrame(() => groupNameRefs.current[nextIndex - 1]?.focus())
  }, [configuredGroups, updateGroups])

  const updateGroup = useCallback((index: number, changes: any) => {
    updateGroups(configuredGroups.map((group, groupIndex) => groupIndex === index ? { ...group, ...changes } : group))
  }, [configuredGroups, updateGroups])


  if (knownLength == null || knownWidth == null || knownLength <= 0 || knownWidth <= 0) {
    return <><p role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">Renseignez la longueur et la largeur du véhicule pour configurer les essieux. La configuration existante est conservée.</p>{couplingControls}</>
  }

  return (
    <div className="space-y-2">
      <AxleDiagram vehicle={vehicle!} axles={axles} groups={configuredGroups} length={vehicleLength} width={vehicleWidth} trackWidth={currentConfig.trackWidth ?? null}
        onTrackWidthChange={value => onChange({ ...vehicle, axle_configuration: { ...currentConfig, trackWidth: value ?? undefined } })} activeIndex={activeAxle} onSelect={setActiveAxle} onUpdate={handleUpdateAxle} onUpdateGroup={updateGroup} onAdd={handleAddAxle} onRemove={handleRemoveAxle} mode={mode} nameRefs={axleNameRefs}
        onCouplingChange={(field, value) => { if (mode !== 'view') onChange(updateVehicleCoupling(vehicle!, field, value)) }} />

      <div className="rounded-lg p-2.5 space-y-2" style={{ background: 'var(--paper)', border: '1px solid var(--border)' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <button type="button" aria-expanded={groupsOpen} onClick={() => setGroupsOpen(open => !open)} className="rounded text-xs font-semibold focus-visible:outline-orange-500" style={{ color: 'var(--text)' }}>Configurer les groupes ({configuredGroups.length})</button>
          </div>
          {mode === 'edit' && (
            <div className="flex gap-2">
              <button ref={addGroupRef} type="button" aria-label="Ajouter un groupe d’essieux" onClick={addGroup} className="min-h-7 px-2.5 py-1 text-xs font-semibold rounded-lg border" style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>Ajouter</button>
              {configuredGroups.length === 0 && axleGroups.length > 0 && <button type="button" onClick={addDetectedGroups} className="px-2.5 py-1 text-xs font-semibold rounded-lg border" style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>Détecter</button>}
            </div>
          )}
        </div>
        {groupsOpen && (configuredGroups.length === 0 ? (
          <p className="text-xs rounded-lg p-3" style={{ background: 'var(--border-soft, rgba(0,0,0,0.03))', color: 'var(--muted)' }}>
            Aucun groupe enregistré. La détection propose les tandem et tridem selon l'espacement des essieux ; confirmez ensuite leurs limites.
          </p>
        ) : configuredGroups.map((group: any, index: number) => (
          <div key={index} data-group-config={index} className="rounded-lg border p-2 space-y-1.5" style={{ borderColor: 'var(--border)' }}>
            <div className="flex min-w-0 items-center gap-1.5">
              <input ref={element => { groupNameRefs.current[index] = element }} aria-label="Nom du groupe" value={group.id || ''} disabled={mode === 'view'} onChange={e => updateGroup(index, { id: e.target.value })} className={inputCls + ' min-w-0 flex-1'} placeholder="Nom du groupe" />
              <select aria-label="Type du groupe" value={group.type || 'tandem'} disabled={mode === 'view'} onChange={e => updateGroup(index, { type: e.target.value })} className={selectCls + ' !w-[92px] shrink-0'}>
                <option value="single">Simple</option><option value="tandem">Tandem</option><option value="tridem">Tridem</option>
              </select>
              {mode === 'edit' && <button type="button" aria-label={`Supprimer le groupe ${group.id}`} title="Supprimer ce groupe" onClick={() => {
              updateGroups(configuredGroups.filter((_, groupIndex) => groupIndex !== index))
              requestAnimationFrame(() => {
                if (configuredGroups.length === 1) addGroupRef.current?.focus()
                else groupNameRefs.current[Math.min(index, configuredGroups.length - 2)]?.focus()
              })
              }} className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"><Trash2 aria-hidden="true" size={13} /></button>}
            </div>
            <div role="group" aria-label={`Essieux membres du groupe ${group.id}`} className="flex flex-wrap items-center gap-1">
              <span className="mr-1 text-[10px] text-slate-500 dark:text-slate-400">Essieux</span>
              <span data-group-members-label className="sr-only">{axleMembersLabel(group.axleIds, axles)}</span>
              {numberedAxles(axles).map(({ axle, number }) => <button key={axle.id} type="button" aria-label={`Essieu ${number} · ${axle.id}`} aria-pressed={group.axleIds.includes(axle.id)} disabled={mode === 'view'}
                title={`Essieu ${number} · ${axle.id} · ${formatMeasure(axle.position, 'mm', 'vehicleLength')}`} onClick={() => updateGroup(index, { axleIds: group.axleIds.includes(axle.id) ? group.axleIds.filter((id: string) => id !== axle.id) : [...group.axleIds, axle.id] })}
                className={`h-7 min-w-7 rounded-md border px-1 text-[11px] font-semibold tabular-nums focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 disabled:cursor-default ${group.axleIds.includes(axle.id) ? 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-200' : 'border-slate-200 text-slate-500 dark:border-slate-700 dark:text-slate-400'}`}>{number}</button>)}
            </div>
            <details data-group-source>
              <summary className="cursor-pointer text-[10px] text-slate-500 focus-visible:outline-orange-500 dark:text-slate-400">Source de la limite{group.limitSource ? ' · renseignée' : ' (facultatif)'}</summary>
              <p id={`${groupHelpId}-${index}`} className="my-1 text-[10px] leading-4 text-slate-500 dark:text-slate-400">Origine de la charge maximale saisie : fiche constructeur, carte grise ou référence interne. Cette note ne sélectionne aucune règle de calcul.</p>
              <input aria-label="Source de la limite" aria-describedby={`${groupHelpId}-${index}`} value={group.limitSource || ''} disabled={mode === 'view'} onChange={e => updateGroup(index, { limitSource: e.target.value })} className={inputCls} placeholder="Ex. fiche constructeur, référence du document" />
            </details>
          </div>
        )))}
      </div>
    </div>
  )
}
